import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  Encounter,
  EncounterSection,
  EncounterStatus,
  FieldMapValue,
  getDateTimeString,
} from '@hb/shared'
import { useMemo } from 'react'
import { EncounterVersionEntry, getEncounterVersionHistory } from './amendmentHistory'
import { generateAllSections, renderSection, SectionWrapper } from './renderEncounterSection'

// §1.6.1: these section types render LIVE chart context, not the version's formData snapshot —
// ExamAllergiesView / ExamMedicationsView read useProfile().user.gyn.mergedData (current
// allergies / medications) and PregnancyHeaderView computes GA/EDD/GPAL from the live
// assessment; all three ignore their `data` argument. Inside a panel labeled "Version
// authenticated [date]" they would show TODAY's chart state as if it were the historical
// record — misleading on a legal preservation surface, and the chart-level data isn't in the
// snapshot to render as-of-then. So they are skipped in version panels; the live note (rendered
// elsewhere) shows them correctly by definition.
const LIVE_CONTEXT_SECTION_TYPES = new Set<EncounterSection['type']>([
  'allergies',
  'medications-supplements',
  // 'pregnancy-header',
])

// §1.6.1 "Original text display" for structured notes: the original authenticated version
// (and every superseded version) is preserved and rendered from its own formData snapshot,
// one tap away behind its always-visible metadata line — never removed. Rendering every
// full version inline would bury the current note (per-field diff is a V2 candidate).
const VersionNoteView = ({
  encounter,
  version,
  encountersArr,
}: {
  encounter: Encounter
  version: EncounterVersionEntry
  encountersArr: Encounter[] | null
}) => {
  // Shim the superseded snapshot into the encounter so the existing section pipeline
  // (generateAllSections + renderSection) renders that version exactly as the live note.
  const versionEncounter = useMemo(
    () => ({ ...encounter, data: version.formData ?? {} }) as Encounter,
    [encounter, version],
  )
  const sections = useMemo(
    () =>
      generateAllSections(versionEncounter, encountersArr).filter(
        s => !LIVE_CONTEXT_SECTION_TYPES.has(s.type),
      ),
    [versionEncounter, encountersArr],
  )
  const versionData = (version.formData ?? {}) as FieldMapValue
  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="gray.200"
      _dark={{ borderColor: 'gray.600' }}
      borderRadius={6}
      overflow="hidden">
      <Flex
        align="center"
        gap={2}
        px={4}
        py={2}
        bg="gray.100"
        _dark={{ bg: 'gray.700' }}
        borderBottom="1px solid"
        borderColor="gray.200">
        <Badge colorScheme="gray" fontSize="2xs" px={2} flexShrink={0}>
          {version.kind === 'original' ? 'Original' : 'Superseded'}
        </Badge>
        <Text fontSize="xs" fontWeight={600} color="gray.600" _dark={{ color: 'gray.300' }}>
          {/* §1.6: authentication timestamps render to the second. */}
          Version authenticated {getDateTimeString(
            version.authenticatedOn,
            'short',
            false,
            true,
          )}{' '}
          by {version.providerName}
        </Text>
      </Flex>
      <VStack w="100%" alignItems="flex-start" spacing={0}>
        {sections.map((section, i) => {
          const content = renderSection(section, versionEncounter, versionData)
          if (content === null) return null
          return (
            <SectionWrapper key={i} index={i} isLast={i === sections.length - 1}>
              {content}
            </SectionWrapper>
          )
        })}
      </VStack>
    </Box>
  )
}

const VersionMetadataLine = ({ version }: { version: EncounterVersionEntry }) => (
  <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }} textAlign="left">
    {version.kind === 'original' ? (
      <>
        {/* §1.6: authentication timestamps render to the second. */}
        Originally authenticated {getDateTimeString(
          version.authenticatedOn,
          'short',
          false,
          true,
        )}{' '}
        by {version.providerName}
      </>
    ) : (
      // §1.6.1 "Amendment display": Amended [date] [time] by [provider name] — Reason: [reason]
      <>
        Amended {getDateTimeString(version.authenticatedOn, 'short', false, true)} by{' '}
        {version.providerName} — Reason: {version.reason ?? 'not recorded'}
      </>
    )}
    {version.isCurrent ? (
      <Text as="span" color="gray.400" _dark={{ color: 'gray.500' }}>
        {' '}
        (current version — shown below)
      </Text>
    ) : null}
  </Text>
)

/**
 * §1.6.1 amendment record. Renders only when the note has recorded amendments or a
 * pending (not yet re-authenticated) amendment — unamended notes get no amendment
 * chrome. Muted, protective-record treatment: one always-visible metadata line per
 * amendment (newest first), each superseded version expandable to its full note.
 */
export const EncounterAmendmentsSection = ({
  encounter,
  encountersArr,
}: {
  encounter: Encounter
  encountersArr: Encounter[] | null
}) => {
  const versions = useMemo(() => getEncounterVersionHistory(encounter), [encounter])
  const hasPendingAmendment =
    encounter.status === EncounterStatus.AUTHENTICATED && !!encounter.draftData

  if (versions.length === 0 && !hasPendingAmendment) return null

  return (
    <Box w="100%">
      {versions.length > 0 ? (
        <>
          <Text
            fontSize="xs"
            color="gray.500"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="0.04em">
            Amendment History
          </Text>
          <Accordion allowMultiple mt={1} borderColor="transparent">
            {versions.map((version, i) => (
              <AccordionItem key={i} border="none">
                {version.formData ? (
                  <>
                    <AccordionButton
                      px={1}
                      py={1}
                      gap={2}
                      borderRadius={4}
                      _hover={{ bg: 'blackAlpha.50' }}>
                      <Box flex={1} minW={0}>
                        <VersionMetadataLine version={version} />
                      </Box>
                      <AccordionIcon color="gray.400" />
                    </AccordionButton>
                    <AccordionPanel px={1} pt={1} pb={2}>
                      <VersionNoteView
                        encounter={encounter}
                        version={version}
                        encountersArr={encountersArr}
                      />
                    </AccordionPanel>
                  </>
                ) : (
                  // The current version is the live note rendered below — no expansion needed.
                  <Box px={1} py={1}>
                    <VersionMetadataLine version={version} />
                  </Box>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        </>
      ) : null}
      {hasPendingAmendment ? (
        <Text fontSize="xs" color="gray.500" fontStyle="italic" px={1} pt={versions.length ? 1 : 0}>
          Amendment in progress — not yet authenticated
        </Text>
      ) : null}
    </Box>
  )
}
